import classNames from 'classnames';
import React, { FC, useState, FocusEvent, ChangeEvent } from 'react';

import { IcSTimeIcon } from '@gem/icons';
import dateService from '@gem/services/date-service';
import { platformInfo } from '@gem/utils';

import { useInputClassNames } from '../hooks/useInputClassNames/useInputClassNames';
import { FieldProps } from '../types';
import styles from './TimePicker.style.m.css';
import { prepareValue } from './prepareValue';

interface ITimePickerProps extends Omit<FieldProps, 'onChange'> {
  readOnly?: boolean;
  value: string;
  onChange: (value: string) => void;
  step?: number;
  min?: string;
  max?: string;
}

export const TimePicker: FC<ITimePickerProps> = React.forwardRef((props, ref) => {
  const {
    message,
    className,
    value,
    label,
    placeholder,
    hasError,
    hasSuccess,
    isDisabled,
    onFocus,
    onBlur,
    readOnly,
    onChange,
    step,
    min = '',
    max = '',
    ...inputProps
  } = props;
  const isHour12 = dateService.isHour12(true);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const handleFocus = (event: FocusEvent<HTMLInputElement>): void => {
    setIsFocused(true);
    onFocus?.(event);
  };
  const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
    setIsFocused(false);
    onBlur?.(event);
  };

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onChange(e.target.value || '00:00');
  };

  const {
    containerClassName,
    contentClassName,
    inputClassName,
    messageClassName,
    labelClassName,
    inputWrapperClassName,
  } = useInputClassNames({
    isFocused,
    isLabel: !!label,
    isSuccess: hasSuccess,
    isError: hasError,
    isDisabled: isDisabled,
    placeholder,
    value,
  });

  const containerClassNames = classNames(
    containerClassName,
    styles.container,
    { [styles.hour12]: isHour12, [styles.hour24]: !isHour12 },
    className
  );
  const contentClassNames = classNames(contentClassName, styles.content);
  const inputClassNames = classNames(
    inputClassName,
    { [styles.inputFlex]: platformInfo.isSafari },
    styles.input
  );
  const preparedValue = prepareValue(value, min, max);

  const inputHandler = (event: ChangeEvent<HTMLInputElement>): void => {
    event.currentTarget.value = prepareValue(event.currentTarget.value, min, max);
  };

  return (
    <div className={containerClassNames}>
      <div className={contentClassNames}>
        <label className={inputWrapperClassName}>
          {label ? <span className={labelClassName}>{label}</span> : null}
          <input
            type="time"
            {...inputProps}
            step={step}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={inputClassNames}
            value={preparedValue}
            onInput={inputHandler}
            onChange={handleOnChange}
            disabled={isDisabled}
            autoComplete="off"
            ref={ref}
            readOnly={readOnly}
            min={min || ''}
            max={max || ''}
          />
          <div className={styles.inputIcon}>
            <IcSTimeIcon />
          </div>
        </label>
      </div>
      {message && <div className={messageClassName}>{message}</div>}
    </div>
  );
});
